// ./src/components/features/Events/accessRule.ts
//
// The website visibility rule as the editors hold it — pure (no React), so the block form builder and the wizard can use
// it too. The control is AccessRuleFields; the rule the server enforces is eventsmagic-api boundary/pageAccess.ts.

export type AccessRuleLevel = "public" | "guest" | "conditional";

/**
 * WHEN it shows, by the event's time (ROADMAP A2e #92, owner 2026-10-03: "show all the time, only before, only during or
 * only after event") — a second axis beside who. The rule the server enforces is eventsmagic-api boundary/pageWhen.ts.
 */
export type AccessRuleWhen = "always" | "before" | "during" | "after";

export const ACCESS_RULE_WHEN_VALUES: AccessRuleWhen[] = ["always", "before", "during", "after"];

export interface AccessRuleValue {
  level: AccessRuleLevel;
  rsvpStatuses: string[];
  guestGroupIds: string[];
  when: AccessRuleWhen;
}

export const PUBLIC_ACCESS_RULE: AccessRuleValue = { level: "public", rsvpStatuses: [], guestGroupIds: [], when: "always" };

/** A stored rule (page or widget columns, possibly JSON text or null) as the control's value. */
export const accessRuleOf = (stored: {
  accessLevel?: string | null;
  accessRsvpStatuses?: unknown;
  accessGuestGroupIds?: unknown;
  visibleWhen?: string | null;
}): AccessRuleValue => {
  const list = (v: unknown): string[] => {
    let raw = v;

    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch {
        return [];
      }
    }

    return Array.isArray(raw) ? raw.map((x) => String(x ?? "")).filter(Boolean) : [];
  };
  const level = String(stored.accessLevel ?? "").trim().toLowerCase();
  const when = String(stored.visibleWhen ?? "").trim().toLowerCase() as AccessRuleWhen;

  return {
    level: level === "guest" || level === "conditional" ? level : "public",
    rsvpStatuses: list(stored.accessRsvpStatuses),
    guestGroupIds: list(stored.accessGuestGroupIds),
    // ⓘ A value this editor does not know reads as "always" here — and the server keeps such a page CLOSED (fail shut),
    //   so saving it puts it back to a value both agree on.
    when: ACCESS_RULE_WHEN_VALUES.includes(when) ? when : "always",
  };
};

/**
 * The columns a rule saves as — the criteria are cleared unless the rule is conditional (a hidden choice must not apply).
 * ⚠ Ticket types are cleared ALWAYS: the API supports them as a third criterion, but no editor offers them, so a value set
 *   some other way would narrow the page invisibly — the host could neither see nor remove it.
 */
export const accessRuleInput = (
  rule: AccessRuleValue,
): {
  accessLevel: AccessRuleLevel;
  accessRsvpStatuses: string[];
  accessGuestGroupIds: string[];
  accessTicketTypeIds: string[];
  visibleWhen: Exclude<AccessRuleWhen, "always"> | null;
} => ({
  accessLevel: rule.level,
  accessRsvpStatuses: rule.level === "conditional" ? rule.rsvpStatuses : [],
  accessGuestGroupIds: rule.level === "conditional" ? rule.guestGroupIds : [],
  accessTicketTypeIds: [],
  // Always is stored as nothing (null): the server reads both the same, and a row that never had a time keeps none.
  visibleWhen: rule.when === "always" ? null : rule.when,
});

/** Where the group picker stands — what it may say and whether it can be used. */
export type GroupPickerMode = "loading" | "error" | "empty" | "ready";

/** The read behind the group picker: what is on screen, whether a read is out, and how the last one ended. */
export interface GroupOptionsState<O> {
  options: O[];
  /** A read is in flight — the first one, or a Try again. */
  loading: boolean;
  /** At least one read has finished (failed or not). */
  isLoaded: boolean;
  error: unknown | null;
}

export type GroupOptionsAction<O> = { type: "start" } | { type: "done"; options: O[] } | { type: "fail"; error: unknown } | { type: "reset" };

export const initialGroupOptionsState = <O>(): GroupOptionsState<O> => ({ options: [], loading: false, isLoaded: false, error: null });

/**
 * ⚠ A read that is OUT is "loading", whatever came before — "Try again" after a failure used to show "This event has no
 * guest groups yet" for the whole retry (isLoaded stayed true over an empty list), telling the host to go and create
 * groups that already exist. A failure keeps the options last shown.
 */
export const groupOptionsReducer = <O>(state: GroupOptionsState<O>, action: GroupOptionsAction<O>): GroupOptionsState<O> => {
  // Another event: nothing read so far belongs to it.
  if (action.type === "reset") {
    return initialGroupOptionsState<O>();
  }

  if (action.type === "start") {
    return { ...state, loading: true, error: null };
  }

  if (action.type === "done") {
    return { options: action.options, loading: false, isLoaded: true, error: null };
  }

  return { ...state, loading: false, isLoaded: true, error: action.error };
};

export const groupPickerMode = (state: { loading: boolean; isLoaded: boolean; error: unknown | null; options: unknown[] }): GroupPickerMode => {
  if (state.loading || (!state.isLoaded && !state.error)) {
    return "loading";
  }

  if (state.error) {
    return "error";
  }

  return state.options.length === 0 ? "empty" : "ready";
};

/** One heading of the group picker: a set's name and its groups. */
export interface GroupOptionGroup {
  group: string;
  items: { value: string; label: string }[];
}

/**
 * The picker's options with every CHOSEN id it does not know added under its own heading, so a pill never shows a raw
 * id. Mantine prints the VALUE of a pill it has no option for — a deleted group (or one of another event) read as a
 * UUID, and was saved again unnoticed. Once the groups have been read, such an id is a "Removed group" the host can take
 * out (the server refuses it on save); before that, it is just not loaded yet.
 */
export const withChosenGroups = (options: GroupOptionGroup[], chosen: string[], mode: GroupPickerMode): GroupOptionGroup[] => {
  const known = new Set(options.flatMap((g) => g.items.map((i) => i.value)));
  const unknown = chosen.filter((id) => !known.has(id));

  if (unknown.length === 0) {
    return options;
  }

  const settled = mode === "ready" || mode === "empty";
  const label = settled ? "Removed group" : mode === "error" ? "Group (couldn't load)" : "Loading…";

  return [...options, { group: settled ? "No longer available" : "Chosen", items: unknown.map((value) => ({ value, label })) }];
};
